/**
 * Mesclagem de estilos: padrão da camada → predefinição → personalizações.
 *
 * O store guarda só as personalizações que diferem da base (padrão da camada
 * já transformado pela predefinição escolhida). Isso mantém o link curto e
 * faz "Restaurar padrão" ser simplesmente apagar a personalização.
 */
import { STYLE_FIELDS, editableKeys, getLayerDef, getPreset } from './catalog';
import type { LayerStyle, PresetId, StyleKey, StyleOverride, VectorLayerId } from './types';

const HEX_RE = /^#[0-9a-f]{6}$/;

/** Estilo base de uma camada sob uma predefinição (sem personalizações). */
export function baseStyle(id: VectorLayerId, preset: PresetId): LayerStyle {
  const def = getLayerDef(id);
  return getPreset(preset).apply(id, { ...def.defaultStyle });
}

/** Estilo efetivo = base da predefinição + personalizações. */
export function resolveStyle(id: VectorLayerId, preset: PresetId, override?: StyleOverride): LayerStyle {
  const base = baseStyle(id, preset);
  return override ? { ...base, ...sanitizeOverride(id, override) } : base;
}

/** Compara dois valores de campo de estilo (números com tolerância). */
export function sameValue(key: StyleKey, a: unknown, b: unknown): boolean {
  const spec = STYLE_FIELDS[key];
  if (spec.kind === 'number' && typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 1e-6;
  }
  if (spec.kind === 'color' && typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
}

/**
 * Normaliza um valor de campo: cor → hex minúsculo; número → faixa e passo;
 * booleano → boolean. Retorna `undefined` se o valor for inválido.
 */
export function normalizeValue<K extends StyleKey>(key: K, value: unknown): LayerStyle[K] | undefined {
  const spec = STYLE_FIELDS[key];
  if (spec.kind === 'color') {
    if (typeof value !== 'string') return undefined;
    const hex = value.trim().toLowerCase();
    return (HEX_RE.test(hex) ? hex : undefined) as LayerStyle[K] | undefined;
  }
  if (spec.kind === 'boolean') {
    return (typeof value === 'boolean' ? value : undefined) as LayerStyle[K] | undefined;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const clamped = Math.min(spec.max, Math.max(spec.min, value));
  // Arredonda para a precisão gravada na URL (evita 0.30000000000000004).
  return (Math.round(clamped * spec.scale) / spec.scale) as LayerStyle[K];
}

/** Descarta campos desconhecidos, não editáveis na camada ou inválidos. */
export function sanitizeOverride(id: VectorLayerId, override: StyleOverride): StyleOverride {
  const out: StyleOverride = {};
  for (const key of editableKeys(id)) {
    if (!(key in override)) continue;
    const v = normalizeValue(key, override[key]);
    if (v !== undefined) (out as Record<StyleKey, unknown>)[key] = v;
  }
  return out;
}

/**
 * Mantém só os campos editáveis que diferem da base. Retorna `undefined`
 * quando não sobra nada (camada no padrão).
 */
export function pruneOverride(id: VectorLayerId, preset: PresetId, override: StyleOverride): StyleOverride | undefined {
  const base = baseStyle(id, preset);
  const clean = sanitizeOverride(id, override);
  const out: StyleOverride = {};
  let n = 0;
  for (const key of Object.keys(clean) as StyleKey[]) {
    if (!sameValue(key, clean[key], base[key])) {
      (out as Record<StyleKey, unknown>)[key] = clean[key];
      n++;
    }
  }
  return n ? out : undefined;
}

/** Igualdade rasa de estilos (usada para evitar reaplicar estilos iguais). */
export function sameStyle(a: LayerStyle, b: LayerStyle, keys?: readonly StyleKey[]): boolean {
  const ks = keys ?? (Object.keys(STYLE_FIELDS) as StyleKey[]);
  return ks.every((k) => sameValue(k, a[k], b[k]));
}

// ---------------------------------------------------------------------------
// Cores
// ---------------------------------------------------------------------------

/** "#rrggbb" + alfa → "rgba(r,g,b,a)". */
export function hexToRgba(hex: string, alpha: number): string {
  const h = HEX_RE.test(hex.toLowerCase()) ? hex.slice(1) : 'ffffff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 1000) / 1000;
  return `rgba(${r},${g},${b},${a})`;
}

/** Luminância relativa (0–1) de uma cor hex. */
export function luminance(hex: string): number {
  const h = HEX_RE.test(hex.toLowerCase()) ? hex.slice(1) : 'ffffff';
  const ch = [0, 2, 4].map((i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** Halo que contrasta com a cor do texto: escuro para textos claros e vice-versa. */
export function haloFor(textHex: string): string {
  return luminance(textHex) > 0.35 ? 'rgba(6,10,18,0.88)' : 'rgba(255,255,255,0.9)';
}
